import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Lock, KeyRound, Loader2, CheckCircle2, Gift, Phone, BadgeCheck, AlertCircle, User, Hash, Mail, Calendar, LogIn as LogInIcon, Clock, Sword } from 'lucide-react';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
  resetSub,
  linkCellphoneSend,
  linkCellphoneVerify,
  getAccount,
} from '@/shared/services/accountService';
import {
  getDiscordStatus,
  startDiscordLink,
  type DiscordLinkStatus,
} from '@/shared/services/discordService';
import { useToast } from '@/shared/contexts/ToastContext';
import { forgotPassword } from '@/shared/services/authServices';
import { onlyDigits, COUNTRIES, type Country, toE164, phonePlaceholder, formatPhone } from '@/shared/utils/phone';
import CountrySelector from '@/shared/components/CountrySelector';

/** Logo do Discord (lucide nao tem icone de marca). */
function DiscordIcon({ size = 18, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3.2a.074.074 0 0 0-.079.037c-.34.6-.717 1.38-.98 1.99a18.27 18.27 0 0 0-5.487 0 12.6 12.6 0 0 0-.997-1.99.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 4.7 4.369a.07.07 0 0 0-.032.027C2.27 7.94 1.62 11.42 1.94 14.86a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127c-.598.349-1.22.645-1.873.891a.076.076 0 0 0-.04.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.029ZM8.02 12.766c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.176 1.094 2.157 2.418 0 1.334-.955 2.419-2.157 2.419Zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.176 1.094 2.157 2.418 0 1.334-.946 2.419-2.157 2.419Z" />
    </svg>
  );
}

/** Linha label/value reutilizavel no grid de info da conta. */
function InfoItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 w-7 h-7 rounded-md bg-[#8fb9de]/10 border border-[#8fb9de]/20 flex items-center justify-center shrink-0 text-[#8fb9de]">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold text-white/65 uppercase tracking-wider">{label}</p>
        <p className="text-sm font-semibold text-white/90 truncate mt-0.5" title={value}>{value}</p>
      </div>
    </div>
  );
}

function formatPlayTime(minutes?: number) {
  if (!minutes || minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

// Mensagens do retorno do OAuth (?discord=<status>)
const DISCORD_RESULT: Record<string, ['success' | 'error' | 'info', string]> = {
  ok: ['success', 'Discord vinculado com sucesso! Seus dias de premium foram creditados.'],
  ok_no_reward: ['info', 'Discord vinculado. O bonus de premium ja havia sido utilizado antes.'],
  already_linked: ['info', 'Sua conta ja estava vinculada ao Discord.'],
  already_used: ['error', 'Esta conta do Discord ja esta vinculada a outro jogador.'],
  not_member: ['error', 'Voce precisa estar no servidor oficial do Liberty no Discord para vincular.'],
  denied: ['error', 'Autorizacao cancelada.'],
  invalid_state: ['error', 'A sessao expirou. Tente vincular novamente.'],
  exchange_failed: ['error', 'Falha ao comunicar com o Discord. Tente novamente.'],
  fetch_failed: ['error', 'Nao foi possivel obter seus dados do Discord. Tente novamente.'],
  disabled: ['error', 'A integracao com o Discord esta indisponivel no momento.'],
  error: ['error', 'Erro ao vincular o Discord. Tente novamente.'],
};

export default function AccountPage() {
  const { account } = useAuth();
  const { notify } = useToast();

  // Change password -- por CODIGO (2026-09-26: era link por email). Mesmo
  // /forgotPassword do login, com o ID da conta logada: manda o codigo pro
  // celular (ou email), confere, e define a senha nova.
  const [passStep, setPassStep] = useState<'start' | 'code' | 'newpass'>('start');
  const [passCode, setPassCode] = useState('');
  const [passNew, setPassNew] = useState('');
  const [passConfirm, setPassConfirm] = useState('');
  const [passDest, setPassDest] = useState('');
  const [passCooldown, setPassCooldown] = useState(0);
  const [passError, setPassError] = useState<string | null>(null);
  const [passLoading, setPassLoading] = useState(false);

  // Reset sub
  const [subPassword, setSubPassword] = useState('');
  const [showSubPass, setShowSubPass] = useState(false);
  const [subLoading, setSubLoading] = useState(false);

  // Discord
  const [discord, setDiscord] = useState<DiscordLinkStatus | null>(null);
  const [discordLoading, setDiscordLoading] = useState(false);

  // Vincular Celular
  // Reflete o que o backend disse — atualizado apos verify pra esconder o card
  const [acctCell, setAcctCell] = useState<{ cellphone?: string | null; isCellphoneVerified: boolean } | null>(null);
  const [country, setCountry] = useState<Country>(COUNTRIES.find((c) => c.iso === 'br')!);
  const [phoneDigits, setPhoneDigits] = useState('');
  const [cellStep, setCellStep] = useState<'form' | 'code'>('form');
  const [cellCode, setCellCode] = useState('');
  const [cellLoading, setCellLoading] = useState(false);
  const [cellCooldown, setCellCooldown] = useState(0);
  const [cellError, setCellError] = useState<string | null>(null);
  const phoneE164 = toE164(country, phoneDigits);
  const cellCodeValid = cellCode.length === 6;

  // sincroniza estado local quando account carrega
  useEffect(() => {
    if (account) {
      setAcctCell({
        cellphone: account.cellphone,
        isCellphoneVerified: !!account.isCellphoneVerified,
      });
    }
  }, [account]);

  // cooldown tick
  const cooldownTimer = useRef<number | null>(null);
  useEffect(() => {
    if (cellCooldown <= 0) return;
    cooldownTimer.current = window.setInterval(() => setCellCooldown((s) => s - 1), 1000);
    return () => { if (cooldownTimer.current) window.clearInterval(cooldownTimer.current); };
  }, [cellCooldown]);

  const handleSendCellCode = async () => {
    setCellError(null);
    if (!phoneE164) { setCellError('Informe um celular valido.'); return; }
    setCellLoading(true);
    try {
      await linkCellphoneSend(phoneE164);
      setCellStep('code');
      setCellCode('');
      setCellCooldown(60);
    } catch (e: any) {
      const msg = e?.detail?.Message || e?.Message || e?.message || 'Falha ao enviar SMS.';
      setCellError(msg);
      notify('error', msg);
    } finally {
      setCellLoading(false);
    }
  };

  const handleVerifyCellCode = async () => {
    setCellError(null);
    if (!cellCodeValid) { setCellError('Digite o codigo completo (6 digitos).'); return; }
    setCellLoading(true);
    try {
      await linkCellphoneVerify(phoneE164, cellCode);
      notify('success', 'Celular vinculado e verificado!');
      // re-fetch da conta pra atualizar status global
      try {
        const updated = await getAccount();
        setAcctCell({
          cellphone: updated.cellphone,
          isCellphoneVerified: !!updated.isCellphoneVerified,
        });
      } catch { /* ignora — re-fetch best effort */ }
      // reset state pra esconder o card
      setCellStep('form');
      setCellCode('');
      setPhoneDigits('');
    } catch (e: any) {
      const msg = e?.detail?.Message || e?.Message || e?.message || 'Codigo invalido.';
      setCellError(msg);
      notify('error', msg);
    } finally {
      setCellLoading(false);
    }
  };

  const handleResendCellCode = async () => {
    if (cellCooldown > 0 || cellLoading) return;
    await handleSendCellCode();
  };

  const loadDiscord = async () => {
    try {
      setDiscord(await getDiscordStatus());
    } catch {
      /* integracao off ou erro — esconde o card */
    }
  };

  useEffect(() => {
    // Toast do retorno do OAuth do Discord (?discord=<status>)
    const params = new URLSearchParams(window.location.search);
    const result = params.get('discord');
    if (result) {
      const msg = DISCORD_RESULT[result];
      if (msg) notify(msg[0], msg[1]);
      // limpa a query da URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    void loadDiscord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (passCooldown <= 0) return;
    const t = setInterval(() => setPassCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [passCooldown]);

  // o erro do servidor chega como {detail: {Status, Message}}
  const passErrMsg = (e: any, fallback: string) => {
    const b = (e?.detail && typeof e.detail === 'object') ? e.detail : e;
    return String(b?.Message || b?.message || e?.message || fallback);
  };

  const passSendCode = async () => {
    if (!account?.id || passLoading || passCooldown > 0) return;
    setPassLoading(true);
    setPassError(null);
    try {
      const res: any = await forgotPassword({ contact: account.id });
      setPassDest(String(res?.destination ?? res?.Destination ?? ''));
      setPassCooldown(Number(res?.retryAfterSeconds ?? res?.RetryAfterSeconds ?? 60));
      setPassCode('');
      setPassStep('code');
    } catch (err: any) {
      setPassError(passErrMsg(err, 'Não foi possível enviar o código.'));
    } finally {
      setPassLoading(false);
    }
  };

  const passVerifyCode = async () => {
    if (!account?.id || passCode.length !== 6) { setPassError('Digite o código completo (6 dígitos).'); return; }
    setPassLoading(true);
    setPassError(null);
    try {
      await forgotPassword({ contact: account.id, code: passCode });
      setPassStep('newpass');
    } catch (err: any) {
      setPassError(passErrMsg(err, 'Código inválido ou expirado.'));
    } finally {
      setPassLoading(false);
    }
  };

  const passSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account?.id) return;
    if (passNew.length < 6) { setPassError('A senha deve ter no mínimo 6 caracteres.'); return; }
    if (passNew !== passConfirm) { setPassError('As senhas não conferem.'); return; }
    setPassLoading(true);
    setPassError(null);
    try {
      await forgotPassword({ contact: account.id, code: passCode, newPassword: passNew });
      notify('success', 'Senha alterada com sucesso!');
      setPassStep('start'); setPassCode(''); setPassNew(''); setPassConfirm('');
    } catch (err: any) {
      setPassError(passErrMsg(err, 'Não foi possível alterar a senha.'));
    } finally {
      setPassLoading(false);
    }
  };

  const handleResetSub = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subPassword.trim()) return;
    setSubLoading(true);
    try {
      const res = await resetSub(subPassword);
      notify('success', res.message || 'Sub password resetado!');
      setSubPassword('');
    } catch (err: any) {
      notify('error', err?.message || 'Erro ao resetar sub password');
    } finally {
      setSubLoading(false);
    }
  };

  const handleLinkDiscord = async () => {
    setDiscordLoading(true);
    try {
      const res = await startDiscordLink();
      // redireciona o browser pro fluxo de autorizacao do Discord
      window.location.href = res.url;
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro ao iniciar a vinculacao');
      setDiscordLoading(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-bold text-white mb-6">Gerenciar Conta</h1>

      {/* Account Info — hero card unificado (mesmo estilo do antigo Dashboard) */}
      {account && (
        <div className="surface-card rounded-md p-6 mb-6">
          {/* Cabecalho com avatar, nome e status */}
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 rounded-md bg-[#8fb9de]/10 border border-[#8fb9de]/30 flex items-center justify-center shrink-0">
              <User size={28} className="text-[#8fb9de]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-white truncate">{account.userName || account.id}</h2>
                {account.isGM && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-[#fa0000]/15 text-[#fa0000] border border-[#fa0000]/30 uppercase tracking-wider">
                    GM
                  </span>
                )}
              </div>
              <p className="text-xs text-white/70 mt-0.5 truncate">{account.email || 'Sem email cadastrado'}</p>
            </div>
            <div className="shrink-0">
              {acctCell?.isCellphoneVerified ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-green-500/15 text-green-400 border border-green-500/30">
                  <BadgeCheck size={12} /> Verificado
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-yellow-500/15 text-yellow-400 border border-yellow-500/30">
                  <AlertCircle size={12} /> Não verificado
                </span>
              )}
            </div>
          </div>

          {/* Divisor sutil */}
          <div className="h-[1px] w-full bg-gradient-to-r from-[#8fb9de]/25 via-[#8fb9de]/10 to-transparent mb-5" />

          {/* Grid de dados da conta */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoItem icon={<Hash size={14} />} label="ID de Login" value={account.id || '—'} />
            <InfoItem icon={<User size={14} />} label="Nome de Usuário" value={account.userName || '—'} />
            <InfoItem icon={<Mail size={14} />} label="Email" value={account.email || 'Não cadastrado'} />
            <InfoItem icon={<Phone size={14} />} label="Celular"
                      value={acctCell?.cellphone || 'Não vinculado'} />
            <InfoItem icon={<Sword size={14} />} label="Personagens"
                      value={account.characterCount != null ? String(account.characterCount) : '—'} />
            <InfoItem icon={<Calendar size={14} />} label="Conta criada em"
                      value={account.createdAt ? new Date(account.createdAt).toLocaleDateString('pt-BR') : '—'} />
            <InfoItem icon={<LogInIcon size={14} />} label="Último login"
                      value={account.lastLoginAt
                        ? new Date(account.lastLoginAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
                        : 'Nunca'} />
            <InfoItem icon={<Clock size={14} />} label="Tempo de jogo"
                      value={formatPlayTime(account.playTime)} />
          </div>
        </div>
      )}

      {/* Vincular Celular (so aparece se nao tem celular vinculado) */}
      {account && acctCell && !acctCell.isCellphoneVerified && (
        <div className="surface-card rounded-md p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Phone size={18} className="text-[#8fb9de]" />
            <h2 className="text-lg font-bold text-white">Vincular Celular</h2>
          </div>
          <p className="text-xs text-white/65 mb-4 leading-relaxed">
            Vincule um celular pra ganhar o selo <strong className="text-green-400">VERIFICADO</strong>.
          </p>

          {cellStep === 'form' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-white/65 mb-1.5">Celular</label>
                <div className="flex items-center gap-2">
                  <CountrySelector value={country} onChange={(c) => { setCountry(c); setPhoneDigits(''); }} />
                  <input
                    type="tel"
                    inputMode="tel"
                    value={formatPhone(country, phoneDigits)}
                    onChange={(e) => setPhoneDigits(onlyDigits(e.target.value).slice(0, country.maxDigits))}
                    placeholder={phonePlaceholder(country)}
                    className="w-full px-4 py-2.5 rounded-md text-sm"
                  />
                </div>
              </div>
              {cellError && (
                <div className="rounded-md border border-[#FA0000]/30 bg-[#FA0000]/10 px-3 py-2 text-xs text-[#ffd0d0]">
                  {cellError}
                </div>
              )}
              <button
                type="button"
                onClick={handleSendCellCode}
                disabled={cellLoading || !phoneE164}
                className="btn-primary px-6 py-2.5 rounded-md text-sm font-semibold w-full flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                {cellLoading && <Loader2 size={14} className="animate-spin" />}
                Enviar código SMS
              </button>
            </div>
          )}

          {cellStep === 'code' && (
            <div className="space-y-3">
              <div className="rounded-md border border-white/10 bg-white/5 p-3 text-xs text-white/70">
                Enviamos um código para <strong className="text-white">{phoneE164}</strong>. Digite abaixo.
              </div>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={cellCode}
                onChange={(e) => setCellCode(onlyDigits(e.target.value).slice(0, 6))}
                placeholder="000000"
                className="w-full px-4 py-2.5 rounded-md text-base font-bold text-center tracking-[0.4em]"
              />
              <div className="flex items-center justify-between text-xs text-white/55">
                <button
                  type="button"
                  className="hover:text-white/80"
                  onClick={() => { setCellStep('form'); setCellCode(''); setCellError(null); }}
                  disabled={cellLoading}
                >
                  ← Trocar número
                </button>
                <button
                  type="button"
                  className={'hover:text-white/80 ' + (cellCooldown > 0 ? 'opacity-40 pointer-events-none' : '')}
                  onClick={handleResendCellCode}
                  disabled={cellLoading || cellCooldown > 0}
                >
                  {cellCooldown > 0 ? `Reenviar em ${cellCooldown}s` : 'Reenviar código'}
                </button>
              </div>
              {cellError && (
                <div className="rounded-md border border-[#FA0000]/30 bg-[#FA0000]/10 px-3 py-2 text-xs text-[#ffd0d0]">
                  {cellError}
                </div>
              )}
              <button
                type="button"
                onClick={handleVerifyCellCode}
                disabled={!cellCodeValid || cellLoading}
                className="btn-primary px-6 py-2.5 rounded-md text-sm font-semibold w-full flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                {cellLoading && <Loader2 size={14} className="animate-spin" />}
                Confirmar e vincular
              </button>
            </div>
          )}
        </div>
      )}

      {/* Vincular Discord */}
      {discord?.enabled && (
        <div className="surface-card rounded-md p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <DiscordIcon size={18} className="text-[#5865F2]" />
            <h2 className="text-lg font-bold text-white">Vincular Discord</h2>
          </div>

          {discord.linked ? (
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 size={16} className="text-green-400 shrink-0" />
              <span className="text-white/80">
                Vinculado como{' '}
                <span className="text-white font-semibold">
                  {discord.discordUsername || discord.discordId}
                </span>
              </span>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-2 mb-4 text-xs text-white/65">
                <Gift size={15} className="text-[#8fb9de] shrink-0 mt-0.5" />
                <p>
                  Vincule sua conta do Discord e ganhe{' '}
                  <span className="text-[#8fb9de] font-semibold">
                    {discord.rewardDays} dias de premium
                  </span>{' '}
                  gratis. Se voce ja possui um premium ativo (Lendário, Mítico ou Épico),
                  os dias sao adicionados a ele. Caso contrario, voce recebe{' '}
                  <span className="text-white/85 font-semibold">Premium Mítico</span>{' '}
                  por {discord.rewardDays} dias. Voce precisa estar no servidor oficial
                  do Liberty no Discord. O bonus e creditado apenas uma vez.
                </p>
              </div>
              <button
                onClick={handleLinkDiscord}
                disabled={discordLoading}
                className="px-6 py-2.5 rounded-md text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer text-white transition-opacity hover:opacity-90"
                style={{ background: '#5865F2' }}
              >
                {discordLoading ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <DiscordIcon size={16} />
                )}
                Vincular minha conta do Discord
              </button>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reset Password by Email */}
        <div className="surface-card rounded-md p-6">
          <div className="flex items-center gap-2 mb-5">
            <Lock size={18} className="text-[#8fb9de]" />
            <h2 className="text-lg font-bold text-white">Alterar Senha</h2>
          </div>
          {passStep === 'start' && (
            <div className="space-y-4">
              <p className="text-xs text-white/65">
                Enviaremos um código para o celular (ou e-mail) cadastrado na sua conta para confirmar a troca.
              </p>
              <button
                type="button"
                onClick={passSendCode}
                disabled={passLoading || passCooldown > 0}
                className="btn-primary px-6 py-2.5 rounded-md text-sm font-semibold w-full flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                {passLoading && <Loader2 size={14} className="animate-spin" />}
                {passCooldown > 0 ? `Enviar código (${passCooldown}s)` : 'Enviar código'}
              </button>
            </div>
          )}

          {passStep === 'code' && (
            <div className="space-y-3">
              <div className="rounded-md border border-white/10 bg-white/5 p-3 text-xs text-white/70">
                Enviamos um código para <strong className="text-white">{passDest}</strong>. Digite abaixo.
              </div>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={passCode}
                onChange={(e) => setPassCode(onlyDigits(e.target.value).slice(0, 6))}
                placeholder="000000"
                className="w-full px-4 py-2.5 rounded-md text-base font-bold text-center tracking-[0.4em]"
              />
              <div className="flex items-center justify-between text-xs text-white/55">
                <button type="button" className="hover:text-white/80" onClick={() => { setPassError(null); setPassStep('start'); }} disabled={passLoading}>
                  ← Voltar
                </button>
                <button
                  type="button"
                  className={'hover:text-white/80 ' + (passCooldown > 0 ? 'opacity-40 pointer-events-none' : '')}
                  onClick={passSendCode}
                  disabled={passLoading || passCooldown > 0}
                >
                  {passCooldown > 0 ? `Reenviar em ${passCooldown}s` : 'Reenviar código'}
                </button>
              </div>
              <button
                type="button"
                onClick={passVerifyCode}
                disabled={passLoading || passCode.length !== 6}
                className="btn-primary px-6 py-2.5 rounded-md text-sm font-semibold w-full flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                {passLoading && <Loader2 size={14} className="animate-spin" />}
                Verificar código
              </button>
            </div>
          )}

          {passStep === 'newpass' && (
            <form onSubmit={passSave} className="space-y-4">
              <div>
                <label className="block text-xs text-white/65 mb-1.5">Nova senha</label>
                <input
                  type="password"
                  value={passNew}
                  onChange={(e) => setPassNew(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full px-4 py-2.5 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-white/65 mb-1.5">Confirmar senha</label>
                <input
                  type="password"
                  value={passConfirm}
                  onChange={(e) => setPassConfirm(e.target.value)}
                  placeholder="Repita a nova senha"
                  className="w-full px-4 py-2.5 rounded-md text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={passLoading}
                className="btn-primary px-6 py-2.5 rounded-md text-sm font-semibold w-full flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                {passLoading && <Loader2 size={14} className="animate-spin" />}
                Salvar nova senha
              </button>
            </form>
          )}

          {passError && (
            <div className="mt-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              {passError}
            </div>
          )}
        </div>

        {/* Reset Sub Password */}
        <div className="surface-card rounded-md p-6">
          <div className="flex items-center gap-2 mb-5">
            <KeyRound size={18} className="text-[#8fb9de]" />
            <h2 className="text-lg font-bold text-white">Resetar Sub Password</h2>
          </div>
          <p className="text-xs text-white/65 mb-4">
            Confirme sua senha principal para resetar a sub password do jogo.
          </p>

          <form onSubmit={handleResetSub} className="space-y-4">
            <div className="relative">
              <label className="block text-xs text-white/65 mb-1.5">Senha da Conta</label>
              <input
                type={showSubPass ? 'text' : 'password'}
                value={subPassword}
                onChange={e => setSubPassword(e.target.value)}
                placeholder="********"
                className="w-full px-4 py-2.5 rounded-md text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShowSubPass(!showSubPass)}
                className="absolute right-3 top-8 text-white/65 hover:text-white transition-colors cursor-pointer"
              >
                {showSubPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <button
              type="submit"
              disabled={subLoading}
              className="btn-primary px-6 py-2.5 rounded-md text-sm font-semibold w-full flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
            >
              {subLoading && <Loader2 size={14} className="animate-spin" />}
              Resetar Sub Password
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
